// meditationVariants — helpers that scale a base beat sequence to hit a
// target wall-clock duration (typically 5, 10, or 20 minutes) for each
// meditation variant.
//
// Every meditation is authored as a base beat array of `speak`, `pause`,
// `breathe`, and `phase` steps. Speech beats are *fixed* — their real
// duration is whatever ElevenLabs turbo takes to read the text, and we
// can only estimate that ahead of time. Everything else (pauses, breath
// cycle counts) is the flex space we scale up or down to hit the target.
//
// The scaler is intentionally uniform: the same factor multiplies pause
// beat `ms` AND breath beat `cycles` so the practice feels naturally
// dilated or condensed rather than an over-long silence between the same
// short breathing sections.
//
// beat shapes:
//   { type: "speak", text }              — ElevenLabs narrated line
//   { type: "pause", ms }                — held silence
//   { type: "breathe", cycles }          — inhale + exhale rounds
//   { type: "phase", label }             — visual-only transition (~0.8s)
//   also supported: skipOn:[variants], onlyOn:variant on any beat

// ElevenLabs Turbo on our meditation voice settings (stability 0.55,
// style 0.15) delivers a slow, hushed cadence — audibly softer than
// conversational English. Empirically measured on the "Innate
// Abilities" script: real MP3 duration across 20 spoken beats totals
// ~273 s vs ~183 s at the naive 430 ms/word rate, i.e. about 1.5× that
// pace. 640 ms/word is the calibrated constant that matches observed
// ElevenLabs meditation-context playback, so `scaleToTarget()` picks
// pause/breath factors that land the real wall-clock runtime on the
// labeled 5 / 10 / 20 minute targets.
const WORD_MS = 640;
// Punctuation adds real pause weight — commas ~350ms, sentences ~600ms
// under the slower meditation cadence.
const PUNCT_MS = 400;
// One-time settling delay at the start of every read (the 480ms in
// readAloud.js scheduleSpeak) is accounted for once per speak beat.
const OPENING_MS = 480;

// Breath cycle timing — matches the components' INHALE_S / EXHALE_S.
const CYCLE_MS = 10_000;
// Phase transition — a visual-only beat (~0.8s).
const PHASE_MS = 800;

/** Estimated wall-clock duration for a spoken beat under ElevenLabs. */
export function speakMs(text) {
  if (!text) return 0;
  const words = text.trim().split(/\s+/).length;
  const punct = (text.match(/[.,;—…!?]/g) || []).length;
  return Math.round(words * WORD_MS + punct * PUNCT_MS + OPENING_MS);
}

/** Estimated wall-clock duration for any beat. */
export function beatMs(beat) {
  if (!beat) return 0;
  if (beat.type === "speak")   return speakMs(beat.text);
  if (beat.type === "pause")   return beat.ms || 0;
  if (beat.type === "breathe") return (beat.cycles || 1) * CYCLE_MS;
  if (beat.type === "phase")   return PHASE_MS;
  return 0;
}

/** Total estimated wall-clock duration of a beat array. */
export function totalMs(beats) {
  return (beats || []).reduce((acc, b) => acc + beatMs(b), 0);
}

/**
 * Split beats into fixed (speech + phase) vs flexible (pause + breath)
 * components. Flexible components are the ones we scale to hit a target.
 */
function partitionMs(beats) {
  let fixed = 0;
  let pauseMs = 0;
  let breathMs = 0;
  for (const b of beats) {
    if (b.type === "speak" || b.type === "phase") {
      fixed += beatMs(b);
    } else if (b.type === "pause") {
      pauseMs += b.ms || 0;
    } else if (b.type === "breathe") {
      breathMs += (b.cycles || 1) * CYCLE_MS;
    }
  }
  return { fixed, pauseMs, breathMs, flexible: pauseMs + breathMs };
}

/**
 * Apply a uniform scale to all pause and breathe beats. Speech and
 * phase beats are preserved as-is. Beats tagged `skipOn:[variant]` are
 * dropped when that variant is passed; beats tagged `onlyOn:variant`
 * are kept only for that variant.
 *
 * Breath cycle counts are rounded to whole cycles with a floor of 1 so
 * we never drop a breath section entirely on very short variants.
 */
export function scaleBeats(base, factor = 1, variant = null) {
  const out = [];
  for (const beat of base) {
    if (variant && Array.isArray(beat.skipOn) && beat.skipOn.includes(variant)) continue;
    if (beat.onlyOn && beat.onlyOn !== variant) continue;
    if (beat.type === "pause") {
      out.push({ ...beat, ms: Math.round((beat.ms || 0) * factor) });
    } else if (beat.type === "breathe") {
      const raw = (beat.cycles || 1) * factor;
      const cycles = Math.max(1, Math.round(raw));
      out.push({ ...beat, cycles });
    } else {
      out.push(beat);
    }
  }
  return out;
}

/**
 * Return a scaled beat array that lands as close to `targetMs` total
 * duration as arithmetic allows, given the fixed cost of the speech +
 * phase beats.
 *
 * Speech time is fixed by the script, so:
 *     factor = (targetMs - fixed) / flexible
 * Clamped to a sensible floor (0.2×) so we never collapse pauses to
 * zero on aggressively short targets.
 */
export function scaleToTarget(base, targetMs, variant = null) {
  // Materialise the variant-filtered beats first, without any scaling,
  // to see what the fixed and flexible portions actually are for this
  // variant (some beats are skipOn / onlyOn tagged).
  const preview = scaleBeats(base, 1, variant);
  const { fixed, flexible } = partitionMs(preview);
  if (flexible <= 0) return preview;
  const factor = Math.max(0.2, (targetMs - fixed) / flexible);
  const scaled = scaleBeats(base, factor, variant);

  // Breath cycles round to integers, which can leave the practice up to
  // ~10 s short or long. Distribute the residual across pause beats so
  // the final total lands within a second or two of the target.
  const drift = targetMs - totalMs(scaled);
  const pauseIdxs = [];
  for (let i = 0; i < scaled.length; i += 1) {
    if (scaled[i].type === "pause") pauseIdxs.push(i);
  }
  if (pauseIdxs.length && Math.abs(drift) > 500) {
    const each = Math.round(drift / pauseIdxs.length);
    for (const i of pauseIdxs) {
      scaled[i] = { ...scaled[i], ms: Math.max(0, (scaled[i].ms || 0) + each) };
    }
  }
  return scaled;
}
